<template>
    <div class="system-settings-page" :class="{ 'dark-theme': isDarkTheme }">
        <!-- Tab 標籤列 -->
        <Tabs v-model="activeTab" class="settings-tabs">
            <TabPane label="視窗" name="views"></TabPane>
            <TabPane label="設定" name="settings"></TabPane>
        </Tabs>

        <!-- 設定分頁內容 -->
        <div v-show="activeTab === 'settings'" class="settings-content">
            <!-- 地圖類型設定 -->
            <!-- Google 地圖類型 -->
            <div class="setting-row map-type-row">
                <div class="setting-label">
                    <span>地圖</span>
                </div>
                <div class="map-type-buttons">
                    <button
                        v-for="type in googleTypes"
                        :key="type.key"
                        :title="type.title"
                        @click="changeMapType(type.key)"
                        :class="['map-type-btn', { active: currentMapType === type.key }]">
                        <Icon :type="type.icon" />
                        <span>{{ type.title }}</span>
                    </button>
                </div>
            </div>
            <!-- Windy 地圖類型 -->
            <div class="setting-row map-type-row">
                <div class="setting-label">
                    <span>氣候</span>
                </div>
                <div class="map-type-buttons">
                    <button
                        v-for="type in windyTypes"
                        :key="type.key"
                        :title="type.title"
                        @click="changeMapType(type.key)"
                        :class="['map-type-btn', { active: currentMapType === type.key }]">
                        <Icon :type="type.icon" />
                        <span>{{ type.title }}</span>
                    </button>
                </div>
            </div>
            <!-- 無地圖類型 -->
            <div class="setting-row map-type-row">
                <div class="setting-label">
                    <span>背景</span>
                </div>
                <div class="map-type-buttons">
                    <button
                        v-for="type in noMapTypes"
                        :key="type.key"
                        :title="type.title"
                        @click="changeMapType(type.key)"
                        :class="['map-type-btn', { active: currentMapType === type.key }]">
                        <Icon :type="type.icon" />
                        <span>{{ type.title }}</span>
                    </button>
                </div>
            </div>

            <!-- 主題設定 -->
            <div class="setting-row">
                <div class="setting-label">
                    <span>主題</span>
                    <small>{{ isDarkTheme ? '深色' : '白色' }}</small>
                </div>
                <IvuSwitch v-model="isDarkTheme" @on-change="handleThemeChange" size="large">
                    <template #open>深色</template>
                    <template #close>白色</template>
                </IvuSwitch>
            </div>

            <!-- 方向設定 -->
            <div class="setting-row">
                <div class="setting-label">
                    <span>導覽列方向</span>
                    <small>{{ isHorizontal ? '橫向' : '直向' }}</small>
                </div>
                <IvuSwitch v-model="isHorizontal" @on-change="handleOrientationChange" size="large">
                    <template #open>橫向</template>
                    <template #close>直向</template>
                </IvuSwitch>
            </div>

            <!-- 顯示日期 -->
            <div class="setting-row">
                <div class="setting-label">
                    <span>顯示日期</span>
                    <small>{{ showDate ? '開啟' : '關閉' }}</small>
                </div>
                <IvuSwitch v-model="showDate" @on-change="handleShowDateChange" size="large">
                    <template #open>開啟</template>
                    <template #close>關閉</template>
                </IvuSwitch>
            </div>

            <!-- 顯示時間 -->
            <div class="setting-row">
                <div class="setting-label">
                    <span>顯示時間</span>
                    <small>{{ showTime ? '開啟' : '關閉' }}</small>
                </div>
                <IvuSwitch v-model="showTime" @on-change="handleShowTimeChange" size="large">
                    <template #open>開啟</template>
                    <template #close>關閉</template>
                </IvuSwitch>
            </div>

            <!-- 非活動視窗亮度 -->
            <div class="setting-row">
                <div class="setting-label">
                    <span>非活動視窗亮度</span>
                    <small>{{ Math.round(inactiveBrightness * 100) }}%</small>
                </div>
                <div class="slider-container">
                    <Slider
                        v-model="inactiveBrightness"
                        :min="0"
                        :max="1"
                        :step="0.01"
                        @on-change="handleInactiveBrightnessChange"
                        style="width: 200px;">
                    </Slider>
                </div>
            </div>

            <!-- 清除遠端模組緩存 -->
            <div class="setting-row">
                <div class="setting-label">
                    <span>遠端模組緩存</span>
                    <small>清除後將重新載入所有模組</small>
                </div>
                <button class="clear-cache-btn" @click="clearRemoteCache" :disabled="isClearingCache">
                    <Icon :type="isClearingCache ? 'ios-loading' : 'md-refresh'" :class="{ 'spin': isClearingCache }" />
                    <span>{{ isClearingCache ? '清除中...' : '清除緩存' }}</span>
                </button>
            </div>
        </div>

        <!-- 視窗分頁內容 -->
        <div v-show="activeTab === 'views'" class="settings-content">
            <div class="view-list-header">
                <small>{{ allViews.length }} 個</small>
                <button class="refresh-btn" @click="refreshAllViews" title="刷新列表">
                    <Icon type="ios-refresh" />
                </button>
                <button v-if="allViews.length > 0" class="close-all-btn" @click="closeAllViews" title="關閉所有視窗">
                    <Icon type="md-close-circle" />
                    <span>全部關閉</span>
                </button>
            </div>

            <div v-if="allViews.length === 0" class="no-views">
                目前沒有開啟的視窗
            </div>
            <div v-else class="view-list">
                <div v-for="view in allViews" :key="view.id" class="view-item">
                    <div class="view-info">
                        <div class="icon-wrapper">
                            <Icon :type="view.isPinned ? 'ios-pin' : 'md-browsers'"
                                  :class="['view-icon', { 'pinned': view.isPinned }]" />
                            <span v-if="view.snapZone === 'left'" class="status-letter snap" title="左分割">L</span>
                            <span v-else-if="view.snapZone === 'right'" class="status-letter snap" title="右分割">R</span>
                            <span v-else-if="view.snapZone === 'maximize' || view.isMaximized" class="status-letter max" title="最大化">M</span>
                        </div>
                        <span class="view-title">{{ view.customName || view.title || view.routeName }}</span>
                    </div>
                    <div class="view-actions">
                        <button class="action-btn reset-btn" @click="resetViewPosition(view.id)" title="回到預設位置">
                            <Icon type="md-locate" />
                        </button>
                        <button v-if="view.isPinned" class="action-btn unpin-btn" @click="unpinView(view.id)" title="解除釘住">
                            <Icon type="ios-pin-outline" />
                        </button>
                        <button class="action-btn close-btn" @click="closeView(view.id)" title="關閉視窗">
                            <Icon type="md-close" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { eventBus } from '@shared/stores/globalState';
import { useUIStore } from '@shared/stores/uiStore';
import { Icon, Switch as IvuSwitch, Slider, Tabs, TabPane } from 'view-ui-plus';

export default {
    name: 'SystemSettingsPage',
    components: {
        Icon,
        IvuSwitch,
        Slider,
        Tabs,
        TabPane
    },
    setup() {
        const uiStore = useUIStore();
        return { uiStore };
    },
    computed: {
        isDarkTheme: {
            get() {
                return this.uiStore.isDarkMode;
            },
            set(value) {
                this.uiStore.setDarkMode(value);
            }
        }
    },
    watch: {
        activeTab(newTab) {
            if (newTab === 'views') {
                this.loadAllViews();
            }
        }
    },
    data() {
        return {
            activeTab: 'views',
            isHorizontal: true,
            pinnedViews: [],
            allViews: [],
            inactiveBrightness: 0.75, // 預設值 0.75
            showDate: true, // 預設顯示日期
            showTime: true, // 預設顯示時間
            currentMapType: 'satellite', // 當前地圖類型
            googleTypes: [
                { title: '衛星', icon: 'md-globe', key: 'satellite' },
                { title: '街道', icon: 'md-map', key: 'roadmap' },
                { title: '混合', icon: 'md-albums', key: 'hybrid' }
            ],
            windyTypes: [
                { title: '風速', icon: 'md-cloud', key: 'wind' },
                { title: '溫度', icon: 'md-thermometer', key: 'temp' },
                { title: '氣壓', icon: 'md-speedometer', key: 'pressure' }
            ],
            noMapTypes: [
                { title: '圖片', icon: 'ios-image', key: 'image' },
                { title: '純色', icon: 'ios-photos-outline', key: 'solid' }
            ],
            isClearingCache: false
        };
    },
    mounted() {
        // 從 localStorage 讀取當前狀態（預設為水平）
        const savedOrientation = localStorage.getItem('navOrientation');
        if (savedOrientation === 'vertical') {
            this.isHorizontal = false;
        }

        // 主題狀態已由 uiStore 管理，此處不需要讀取 localStorage

        const savedBrightness = localStorage.getItem('inactiveBrightness');
        if (savedBrightness !== null) {
            const brightness = parseFloat(savedBrightness);
            if (!isNaN(brightness) && brightness >= 0 && brightness <= 1) {
                this.inactiveBrightness = brightness;
            }
        }

        const savedShowDate = localStorage.getItem('showDate');
        if (savedShowDate !== null) {
            this.showDate = savedShowDate === 'true';
        }

        const savedShowTime = localStorage.getItem('showTime');
        if (savedShowTime !== null) {
            this.showTime = savedShowTime === 'true';
        }

        // 讀取保存的地圖類型
        const savedMapType = localStorage.getItem('mapType');
        if (savedMapType) {
            this.currentMapType = savedMapType;
        }

        // 監聽外部的設定變化
        eventBus.on('system:nav-orientation-changed', this.handleOrientationChanged);
        eventBus.on('ui:theme-changed', this.handleThemeChanged);
        eventBus.on('ui:map-type-changed', this.handleMapTypeChanged);
        eventBus.on('show-date-changed', this.handleShowDateChanged);
        eventBus.on('show-time-changed', this.handleShowTimeChanged);

        // 獲取視窗列表 - 延遲以確保 MultiViewManager 已完成初始化
        setTimeout(() => {
            this.loadPinnedViews();
            this.loadAllViews();
        }, 600);
        eventBus.on('pinned-views-updated', this.loadPinnedViews);

        // 監聯系統設定打開事件，刷新視窗列表
        eventBus.on('ui:open-settings', this.refreshAllViews);

        // 請求當前地圖類型
        this.requestCurrentMapType();
    },
    beforeUnmount() {
        eventBus.off('system:nav-orientation-changed', this.handleOrientationChanged);
        eventBus.off('ui:theme-changed', this.handleThemeChanged);
        eventBus.off('ui:map-type-changed', this.handleMapTypeChanged);
        eventBus.off('show-date-changed', this.handleShowDateChanged);
        eventBus.off('show-time-changed', this.handleShowTimeChanged);
        eventBus.off('pinned-views-updated', this.loadPinnedViews);
        eventBus.off('ui:open-settings', this.refreshAllViews);
    },
    methods: {
        handleOrientationChange(isHorizontal) {
            console.log('[SystemSettings] 方向變化:', isHorizontal);
            localStorage.setItem('navOrientation', isHorizontal ? 'horizontal' : 'vertical');
            eventBus.emit('system:nav-orientation-change', isHorizontal);
        },
        handleThemeChange(isDark) {
            console.log('[SystemSettings] 主題變化:', isDark);
            // 透過 computed setter (isDarkTheme) 已經調用 uiStore.setDarkMode()
            // uiStore.setDarkMode() 會處理 localStorage、body class、VXE Table 主題和事件發送
        },
        handleOrientationChanged(isHorizontal) {
            this.isHorizontal = isHorizontal;
        },
        handleThemeChanged(isDark) {
            this.isDarkTheme = isDark;
        },
        handleInactiveBrightnessChange(value) {
            console.log('[SystemSettings] 非活動視窗亮度變化:', value);
            localStorage.setItem('inactiveBrightness', value.toString());
            // 事件已移除（無監聽方）- 未來如需要可添加到 eventTypes.js
        },
        handleShowDateChange(show) {
            console.log('[SystemSettings] 顯示日期狀態變化:', show);
            localStorage.setItem('showDate', show.toString());
            eventBus.emit('show-date-change', show);
        },
        handleShowDateChanged(show) {
            this.showDate = show;
        },
        handleShowTimeChange(show) {
            console.log('[SystemSettings] 顯示時間狀態變化:', show);
            localStorage.setItem('showTime', show.toString());
            eventBus.emit('show-time-change', show);
        },
        handleShowTimeChanged(show) {
            this.showTime = show;
        },
        loadPinnedViews() {
            console.log('[SystemSettings] 請求釘住的視窗列表');

            // 先從 localStorage 讀取作為備用
            this.loadPinnedViewsFromStorage();

            // 然後請求 MultiViewManager 提供最新的列表
            eventBus.emit('request-pinned-views');

            // 監聽回應（一次性）
            const handler = (views) => {
                console.log('[SystemSettings] 收到釘住的視窗列表 (from eventBus):', views?.length || 0, views);
                this.pinnedViews = views || [];
                eventBus.off('pinned-views-response', handler);
            };
            eventBus.on('pinned-views-response', handler);

            // 超時保護：如果 2 秒內沒收到回應，保持使用 localStorage 的資料
            setTimeout(() => {
                eventBus.off('pinned-views-response', handler);
            }, 2000);
        },
        refreshPinnedViews() {
            console.log('[SystemSettings] 手動刷新釘住的視窗列表');
            this.loadPinnedViews();
        },
        loadAllViews() {
            try {
                const stored = localStorage.getItem('multiViewManager_views');
                if (!stored) {
                    this.allViews = [];
                    return;
                }

                const viewsData = JSON.parse(stored);
                const views = [];

                Object.entries(viewsData).forEach(([viewId, config]) => {
                    views.push({
                        id: viewId,
                        routeName: config.routeName,
                        title: config.title,
                        customName: config.customName,
                        isPinned: config.isPinned || false,
                        isSnapped: config.isSnapped || false,
                        snapZone: config.snapZone || null,  // 'left', 'right', 'maximize'
                        isMaximized: config.isMaximized || false
                    });
                });

                this.allViews = views;
            } catch (error) {
                console.error('[SystemSettings] 讀取視窗列表失敗:', error);
                this.allViews = [];
            }
        },
        refreshAllViews() {
            console.log('[SystemSettings] 手動刷新所有視窗列表');
            this.loadPinnedViews();
            this.loadAllViews();
        },
        resetViewPosition(viewId) {
            console.log('[SystemSettings] 重設視窗位置:', viewId);
            eventBus.emit('reset-view-position', viewId);
            // 重新載入列表（延遲較長以確保 localStorage 已更新）
            setTimeout(() => {
                this.loadAllViews();
            }, 500);
        },
        loadPinnedViewsFromStorage() {
            try {
                const stored = localStorage.getItem('multiViewManager_views');
                if (!stored) {
                    console.log('[SystemSettings] localStorage 中沒有視窗資料');
                    this.pinnedViews = [];
                    return;
                }

                const viewsData = JSON.parse(stored);
                const pinnedViews = [];

                // 遍歷所有視窗，找出釘住的
                Object.entries(viewsData).forEach(([viewId, config]) => {
                    if (config.isPinned === true) {
                        pinnedViews.push({
                            id: viewId,
                            routeName: config.routeName,
                            title: config.title,
                            customName: config.customName,
                            isPinned: true
                        });
                    }
                });

                console.log('[SystemSettings] 從 localStorage 讀取釘住的視窗:', pinnedViews.length, pinnedViews);
                this.pinnedViews = pinnedViews;
            } catch (error) {
                console.error('[SystemSettings] 讀取 localStorage 失敗:', error);
                this.pinnedViews = [];
            }
        },
        unpinView(viewId) {
            console.log('[SystemSettings] 解除釘住視窗:', viewId);
            eventBus.emit('unpin-view', viewId);
            // 重新載入列表（延遲較長以確保 localStorage 已更新）
            setTimeout(() => {
                this.loadPinnedViews();
                this.loadAllViews();
            }, 500);
        },
        closeView(viewId) {
            console.log('[SystemSettings] 關閉視窗:', viewId);
            eventBus.emit('view:close', viewId);
            // 重新載入列表
            setTimeout(() => {
                this.loadPinnedViews();
                this.loadAllViews();
            }, 300);
        },
        closeAllViews() {
            console.log('[SystemSettings] 關閉所有視窗');
            // 逐一關閉所有視窗
            this.allViews.forEach(view => {
                eventBus.emit('view:close', view.id);
            });
            // 重新載入列表
            setTimeout(() => {
                this.loadPinnedViews();
                this.loadAllViews();
            }, 500);
        },
        // 地圖類型相關方法
        changeMapType(type) {
            console.log('[SystemSettings] 切換地圖類型:', type);
            this.currentMapType = type;
            localStorage.setItem('mapType', type);
            // 發送地圖類型變化事件
            eventBus.emit('ui:map-type-change', type);
        },
        handleMapTypeChanged(type) {
            console.log('[SystemSettings] 收到地圖類型變化:', type);
            // 確保只保存字串
            this.currentMapType = typeof type === 'string' ? (type || 'satellite') : 'satellite';
        },
        requestCurrentMapType() {
            // 請求當前地圖類型
            eventBus.emit('ui:map-type-request');
            // 監聽回應
            const handler = (type) => {
                if (type) {
                    // 確保只保存字串
                    this.currentMapType = typeof type === 'string' ? type : String(type || 'satellite');
                }
                eventBus.off('ui:map-type-response', handler);
            };
            eventBus.on('ui:map-type-response', handler);
            // 超時保護
            setTimeout(() => {
                eventBus.off('ui:map-type-response', handler);
            }, 1000);
        },
        getCurrentMapTypeName() {
            const allTypes = [...this.googleTypes, ...this.windyTypes, ...this.noMapTypes];
            const type = allTypes.find(t => t.key === this.currentMapType);
            return type ? type.title : '未知';
        },
        async clearRemoteCache() {
            if (this.isClearingCache) return;

            this.isClearingCache = true;
            console.log('[SystemSettings] 開始清除遠端模組緩存...');

            try {
                // 1. 清除 Service Worker 緩存
                if ('caches' in window) {
                    const cacheNames = await caches.keys();
                    console.log('[SystemSettings] 找到緩存:', cacheNames);

                    for (const cacheName of cacheNames) {
                        // 清除所有緩存（更徹底）
                        await caches.delete(cacheName);
                        console.log('[SystemSettings] 已刪除緩存:', cacheName);
                    }
                }

                // 2. 註銷並重新註冊 Service Worker
                if ('serviceWorker' in navigator) {
                    const registrations = await navigator.serviceWorker.getRegistrations();
                    for (const registration of registrations) {
                        await registration.unregister();
                        console.log('[SystemSettings] 已註銷 Service Worker');
                    }
                }

                console.log('[SystemSettings] 緩存清除完成，準備重新載入頁面...');

                // 3. 使用 cache-busting 參數強制繞過瀏覽器快取
                setTimeout(() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('_cache_bust', Date.now().toString());
                    window.location.href = url.toString();
                }, 500);

            } catch (error) {
                console.error('[SystemSettings] 清除緩存失敗:', error);
                this.isClearingCache = false;
                // 即使失敗也嘗試重新載入
                const url = new URL(window.location.href);
                url.searchParams.set('_cache_bust', Date.now().toString());
                window.location.href = url.toString();
            }
        }
    }
};
</script>

<style scoped lang="stylus">
.system-settings-page
    width 100%
    height 100%
    padding 10px 12px
    background #fff
    overflow-y auto

.settings-content
    max-width 600px
    margin 0 auto

// 設定列
.setting-row
    display flex
    align-items center
    justify-content space-between
    padding 8px 5px
    border-bottom 1px solid #f0f0f0
    transition all 0.2s ease

    &:hover
        background #fafafa

    &.pinned-section
        padding-bottom 12px
        margin-top 8px
        border-top 1px solid #e0e0e0

    &.map-type-row
        align-items center

.setting-label
    display flex
    flex-direction column
    gap 4px

    &.full-width
        width 100%

    .label-header
        display flex
        align-items center
        justify-content space-between
        width 100%

        > div
            display flex
            flex-direction column
            gap 4px

    span
        color #333
        font-size 15px
        font-weight 500

    small
        color #999
        font-size 13px

.refresh-btn
    width 32px
    height 32px
    border none
    background transparent
    color #666
    cursor pointer
    border-radius 4px
    display flex
    align-items center
    justify-content center
    transition all 0.2s ease
    flex-shrink 0

    &:hover
        background #f0f0f0
        color #333
        transform rotate(180deg)

    &:active
        background #e0e0e0
        transform rotate(180deg) scale(0.95)

// 覆蓋 view-ui-plus IvuSwitch 樣式
:deep(.ivu-switch-large)
    min-width 60px

    &.ivu-switch-checked
        background #5A9FFF
        border-color #5A9FFF

        &:hover
            background #4A8FEF
            border-color #4A8FEF

// 清除緩存按鈕
.clear-cache-btn
    display flex
    align-items center
    gap 6px
    padding 8px 16px
    border 1px solid #ff9500
    background #fff
    border-radius 6px
    cursor pointer
    transition all 0.2s ease
    font-size 13px
    color #ff9500

    i
        font-size 16px

        &.spin
            animation spin 1s linear infinite

    &:hover:not(:disabled)
        background #ff9500
        color #fff
        transform translateY(-1px)
        box-shadow 0 2px 8px rgba(255, 149, 0, 0.3)

    &:active:not(:disabled)
        transform translateY(0)

    &:disabled
        opacity 0.6
        cursor not-allowed

@keyframes spin
    from
        transform rotate(0deg)
    to
        transform rotate(360deg)

// 地圖類型按鈕
.map-type-buttons
    display flex
    flex-wrap wrap
    gap 8px
    justify-content flex-end
    flex 1

.map-type-btn
    max-width 100px
    padding 8px 12px
    border 1px solid #ddd
    background #fff
    border-radius 6px
    cursor pointer
    transition all 0.2s ease
    display flex
    flex-direction row
    align-items center
    justify-content center
    gap 6px
    font-size 12px
    color #666

    i
        font-size 16px
        flex-shrink 0

    span
        font-size 12px
        white-space nowrap

    &:hover
        background #f5f5f5
        border-color #bbb
        transform translateY(-1px)
        box-shadow 0 2px 4px rgba(0, 0, 0, 0.1)

    &.active
        background #5A9FFF
        border-color #5A9FFF
        color #fff
        box-shadow 0 2px 8px rgba(90, 159, 255, 0.3)

        &:hover
            background #4A8FEF
            border-color #4A8FEF

// 分頁樣式
.settings-tabs
    :deep(.ivu-tabs-content)
        display none  // 隱藏空的 TabPane 內容區

// 視窗列表
.view-list-header
    display flex
    align-items center
    gap 12px
    padding 0 5px
    border-bottom 1px solid #f0f0f0

    span
        color #333
        font-size 15px
        font-weight 500

    small
        color #999
        font-size 13px
        flex 1

.close-all-btn
    display flex
    align-items center
    gap 4px
    padding 6px 12px
    border 1px solid #ff4d4f
    background #fff
    border-radius 4px
    cursor pointer
    transition all 0.2s ease
    font-size 12px
    color #ff4d4f

    i
        font-size 14px

    &:hover
        background #ff4d4f
        color #fff

.no-views
    margin 16px 5px
    padding 16px
    text-align center
    color #999
    font-size 14px
    background #f9f9f9
    border-radius 6px
    border 1px dashed #e0e0e0

.view-list
    display flex
    flex-direction column
    gap 8px
    margin 12px 0

.view-item
    display flex
    align-items center
    justify-content space-between
    padding 5px 5px 5px 10px
    background #fafafa
    border-radius 6px
    border 1px solid #e8e8e8
    transition all 0.2s ease

    &:hover
        background #f5f5f5
        border-color #d8d8d8

.view-info
    display flex
    align-items center
    gap 8px
    flex 1
    min-width 0

    .icon-wrapper
        position relative
        width 18px
        height 18px
        flex-shrink 0
        display flex
        align-items center
        justify-content center

    .view-icon
        font-size 16px
        color #999

        &.pinned
            color #ff9500

    .status-letter
        position absolute
        top 0
        left 0
        font-size 10px
        font-weight 700
        width 18px
        height 18px
        display flex
        align-items center
        justify-content center
        border-radius 3px

        &.snap
            background rgba(24, 144, 255, 0.9)
            color #fff

        &.max
            background rgba(82, 196, 26, 0.9)
            color #fff

    .view-title
        color #333
        font-size 13px
        overflow hidden
        text-overflow ellipsis
        white-space nowrap

.view-actions
    display flex
    gap 8px

.action-btn
    display flex
    align-items center
    gap 4px
    padding 6px 12px
    border 1px solid #ddd
    background #fff
    border-radius 4px
    cursor pointer
    transition all 0.2s ease
    font-size 12px
    color #666

    i
        font-size 14px

    &:hover
        border-color #5A9FFF
        color #5A9FFF

    &.reset-btn:hover
        background #5A9FFF
        border-color #5A9FFF
        color #fff

    &.unpin-btn:hover
        background #ff9500
        border-color #ff9500
        color #fff

    &.close-btn:hover
        background #ff4d4f
        border-color #ff4d4f
        color #fff

// 釘住視窗列表 (保留向後相容)
.no-pinned
    margin 16px 20px
    padding 16px
    text-align center
    color #999
    font-size 14px
    background #f9f9f9
    border-radius 6px
    border 1px dashed #e0e0e0

.pinned-list
    display flex
    flex-direction column
    gap 8px
    margin 16px 20px

.pinned-item
    display flex
    align-items center
    justify-content space-between
    padding 12px 16px
    background #fafafa
    border-radius 6px
    border 1px solid #e8e8e8
    transition all 0.2s ease

    &:hover
        background #f5f5f5
        border-color #d8d8d8
        box-shadow 0 2px 4px rgba(0, 0, 0, 0.05)

.pinned-info
    display flex
    align-items center
    gap 10px
    flex 1

    .pinned-icon
        color #ff9500
        font-size 16px
        flex-shrink 0

    .pinned-title
        color #333
        font-size 14px
        font-weight 500

.unpin-btn
    padding 6px 16px
    border 1px solid #ddd
    background #fff
    border-radius 4px
    cursor pointer
    transition all 0.2s ease
    font-size 13px
    color #666
    flex-shrink 0

    &:hover
        background #ff9500
        border-color #ff9500
        color #fff
        transform translateY(-1px)
        box-shadow 0 2px 4px rgba(255, 149, 0, 0.3)

    &:active
        background #e88500
        border-color #e88500
        transform translateY(0)

// ===== Dark Theme 樣式 (帶藍色調) =====
.system-settings-page.dark-theme
    background var(--dark-bg)

    .setting-row
        border-bottom-color #2e3440

        &:hover
            background #1e222a

        &.pinned-section
            border-top-color #2e3440

    .setting-label
        span
            color #e5e9f0

        small
            color #8892a0

    .refresh-btn
        color #b8c0cc

        &:hover
            background #2e3440
            color #fff

        &:active
            background #3b4252

    // 地圖類型按鈕
    .map-type-btn
        background #252a33
        border-color #3b4252
        color #b8c0cc

        &:hover
            background #2e3440
            border-color #4c566a

        &.active
            background #5A9FFF
            border-color #5A9FFF
            color #fff

    // 視窗列表
    .view-list-header
        border-bottom-color #2e3440

        span
            color #e5e9f0

        small
            color #8892a0

    .no-views
        background #1e222a
        border-color #2e3440
        color #8892a0

    .view-item
        background #1e222a
        border-color #2e3440

        &:hover
            background #252a33
            border-color #3b4252

    .view-info
        .view-icon
            color #8892a0

            &.pinned
                color #ff9500

        .view-title
            color #e5e9f0

        .status-letter
            &.snap
                background rgba(24, 144, 255, 0.9)

            &.max
                background rgba(82, 196, 26, 0.9)

    .action-btn
        background #252a33
        border-color #3b4252
        color #b8c0cc

        &:hover
            border-color #5A9FFF
            color #5A9FFF

        &.reset-btn:hover
            background #5A9FFF
            border-color #5A9FFF
            color #fff

        &.unpin-btn:hover
            background #ff9500
            border-color #ff9500
            color #fff

        &.close-btn:hover
            background #ff4d4f
            border-color #ff4d4f
            color #fff

    .close-all-btn
        background #252a33
        border-color #ff4d4f
        color #ff4d4f

        &:hover
            background #ff4d4f
            color #fff

    // 釘住視窗列表
    .no-pinned
        background #1e222a
        border-color #2e3440
        color #8892a0

    .pinned-item
        background #1e222a
        border-color #2e3440

        &:hover
            background #252a33
            border-color #3b4252

    .pinned-info
        .pinned-title
            color #e5e9f0

    .unpin-btn
        background #252a33
        border-color #3b4252
        color #b8c0cc

        &:hover
            background #ff9500
            border-color #ff9500
            color #fff

    // 覆蓋 iView 組件深色樣式
    :deep(.ivu-switch)
        background #3b4252

        &.ivu-switch-checked
            background #5A9FFF

    :deep(.ivu-slider-bar)
        background #5A9FFF

    :deep(.ivu-slider-button-wrap .ivu-slider-button)
        border-color #5A9FFF

    :deep(.ivu-slider-wrap)
        background #3b4252

    // 清除緩存按鈕
    .clear-cache-btn
        background #252a33
        border-color #ff9500
        color #ff9500

        &:hover:not(:disabled)
            background #ff9500
            color #fff
</style>

